import React, {useEffect, useState} from "react";

import {Button} from "primereact/button";
import {Message} from "primereact/message";
import i18n from "../../i18n";

import {formatCurrency, formValid, getActivePriceSendHold, getKeyValue} from "../../utils/Utils";
import {Loading, Tooltip, IndexTemplate} from "../../utils/InlineComponents";

import {Dialog} from "primereact/dialog";
import {ProductService} from "../../service/ProductService";
import {DynamicPricingTemplateService} from "../../service/DynamicPricingTemplateService";

import Number from "../Common/Number";
import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {Dropdown} from "primereact/dropdown";
import {protectionStrategies} from "../../utils/Constants";
import {SelectButton} from "primereact/selectbutton";
import {InputNumber} from "primereact/inputnumber";
import {Checkbox} from "primereact/checkbox";
import {InputSwitch} from "primereact/inputswitch";
import {getWeeksAgo, toDateTimeString} from "../../utils/TimeUtil";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import PricingModeGuide from "../Pricing/PricingModeGuide";
import BuyboxStateBadge from "../Pricing/BuyboxStateBadge";

const ProductReprice = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const {selectedCampaign} = React.useContext(CampaignContext);

    const productService = new ProductService();

    let selectedProduct = {...props.selectedProduct};

    const defaultStrategy = {
        scope: "campaign",
        min_value : 0,
        protection_strategy: "min_value",
        optimum_value : 0,
        reduction_type : "ratio",
        reduction_direction: 1,
        reduction_value : 0,
        is_active : false,
        is_auto_update : false,
        ignore_some_competitors: false,
        no_competitor_strategy: false,
        guarantee_buybox: false,
        buybox_step: null,
        buybox_step_type: 'amount',
        abandon_margin_pct: 0,
        patience_hours: null,
        buybox_upward_probe: false,
        buybox_up_step: null,
        buybox_up_step_type: 'amount',
    };

    const [pricingStrategy, setPricingStrategy] = useState({...defaultStrategy});
    const [appliedSuggestions, setAppliedSuggestions] = useState([]);
    const [showHistory, setShowHistory] = useState(false);

    const [formErrors, setFormErrors] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    // Save runs in-place (button spinner) so the tab isn't fully re-rendered; only
    // the recomputed suggestion values refresh. `loading` stays for the full-form
    // spinner used by the send-price-to-site flow.
    const [saving, setSaving] = useState(false);

    // UI mode layered on top of pricingStrategy.scope: 'template' | 'campaign' | 'product'.
    const [mode, setMode] = useState('product');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(props.selectedProduct ? props.selectedProduct.pricing_template_id : null);
    // The tab opens read-only; "Set Dynamic Pricing" switches to the editor.
    const [editMode, setEditMode] = useState(false);
    // Automatic price sending, paused per product. Saved on its own the moment it
    // is flipped, NOT through the strategy form: the flag is orthogonal to which
    // rule prices the product, and folding it into a strategy save would drag the
    // product off its campaign rule -- the exact thing this feature avoids.
    const [autoUpdatePaused, setAutoUpdatePaused] = useState(!!props.selectedProduct.is_auto_update_paused);
    const [savingAutoUpdate, setSavingAutoUpdate] = useState(false);

    const toggleAutoUpdatePaused = (isPaused) => {
        setSavingAutoUpdate(true);
        setAutoUpdatePaused(isPaused);
        productService.setAutoUpdatePaused(selectedProduct.id, isPaused).then(() => {
            // Keep the row the parent list holds in step, so leaving the detail
            // does not show the stale badge.
            props.selectedProduct.is_auto_update_paused = isPaused;
            toast.current.show({severity: 'success',
                summary: t('reprice.auto_update_sending'),
                detail: isPaused ? t('reprice.auto_update_paused_done') : t('reprice.auto_update_resumed_done')});
        }).catch(e => {
            setAutoUpdatePaused(!isPaused);   // the write failed; do not show it as done
            setError(e); setError(null);
        }).finally(() => setSavingAutoUpdate(false));
    };
    const reductionTypes = [{name:t('reprice.amount'), value: 'amount'}, {name: t('reprice.ratio'), value: 'ratio'}];
    const reductionDirections = [{name:t('reprice.low'), value: 1}, {name: t('reprice.high'), value: -1}];
    const competitors = [{name:t('reprice.all'), value: false}, {name: t('reprice.only_specified'), value: true}];
    const statuses = [{name:t('actions.passive'), value: false}, {name: t('actions.active'), value: true}];
    const updateTypes = [{name:t('reprice.manuel_update'), value: false}, {name: t('reprice.auto_update'), value: true}];
    const noCompetitorTypes = [{name:t('yes_no.no'), value: false}, {name: t('yes_no.yes'), value: true}];
    const buyboxStepTypes = [{name:t('reprice.amount'), value: 'amount'}, {name:t('reprice.ratio'), value: 'percentage'}];
    const buyboxUpStepTypes = [{name:t('reprice.amount'), value: 'amount'}, {name:t('reprice.ratio'), value: 'percentage'}];
    // InputNumber's onValueChange gives null for an emptied field -- keep that as-is
    // for nullable fields (patience_hours, buybox_up_step); abandon_margin_pct is NOT
    // nullable (0 = always chase is the safe/legacy default), so it is coerced back to 0.
    const onNumberFieldChange = (key, value, zeroDefault) => {
        setPricingStrategy(prev => ({...prev, [key]: (zeroDefault && (value === null || value === undefined)) ? 0 : value}));
    };
    const pricingModes = [
        {name: t('reprice.mode_normal'), value: false, desc: t('reprice.mode_normal_desc')},
        {name: t('reprice.mode_buybox'), value: true, desc: t('reprice.mode_buybox_desc')},
    ];
    // Buybox flag from the product's OWN campaign (not the header-selected one) --
    // the product may belong to a different campaign than the one selected up top.
    const isBuyboxCampaign = !!(selectedProduct.campaign && selectedProduct.campaign.settings && selectedProduct.campaign.settings.marketplace_api);

    // Derive pricingStrategy / mode / selectedTemplateId from the product. Called on
    // load and on Cancel (to discard unsaved edits).
    const deriveFromProduct = () => {
        const sp = props.selectedProduct;
        if (!sp) return;
        const isCampaignScope = sp.pricing_strategy && sp.pricing_strategy.reduction_type === "campaign";

        if (sp.pricing_strategy)
            setPricingStrategy({...sp.pricing_strategy,
                is_active: sp.pricing_strategy.is_active ?? false,
                is_auto_update: sp.pricing_strategy.is_auto_update ?? false,
                ignore_some_competitors: sp.pricing_strategy.ignore_some_competitors ?? false,
                scope: isCampaignScope ? "campaign" : "product",
                reduction_type: isCampaignScope ? "ratio" : sp.pricing_strategy.reduction_type,
                reduction_direction: sp.pricing_strategy.reduction_value >= 0 ? 1 : -1,
                reduction_value: Math.abs(sp.pricing_strategy.reduction_value),
                no_competitor_strategy: sp.pricing_strategy.no_competitor_strategy ?? false,
                // Legacy products saved before these fields existed -- default them to
                // the values that reproduce the old one-way-down behavior exactly.
                abandon_margin_pct: sp.pricing_strategy.abandon_margin_pct ?? 0,
                patience_hours: sp.pricing_strategy.patience_hours ?? null,
                buybox_upward_probe: sp.pricing_strategy.buybox_upward_probe ?? false,
                buybox_up_step: sp.pricing_strategy.buybox_up_step ?? null,
                buybox_up_step_type: sp.pricing_strategy.buybox_up_step_type || 'amount',
            });
        else
            setPricingStrategy({...defaultStrategy});

        setSelectedTemplateId(sp.pricing_template_id);
        // A product with no own strategy/template but whose campaign has an active
        // strategy INHERITS it -> open the editor in Campaign mode (From Campaign
        // preselected), since that reflects the product's current effective state.
        const campActive = sp.campaign && sp.campaign.pricing_strategy && sp.campaign.pricing_strategy.is_active;
        if (sp.pricing_template_id) setMode('template');
        else if (isCampaignScope) setMode('campaign');
        else if (!sp.pricing_strategy && campActive) setMode('campaign');
        else setMode('product');
        // Re-read on every product change: the detail screen steps between
        // products without remounting, so a state seeded only at mount would
        // show the previous product's switch position.
        setAutoUpdatePaused(!!sp.is_auto_update_paused);
    };

    useEffect ( ()=>{
        selectedProduct = {...props.selectedProduct};
        deriveFromProduct();
        setEditMode(false);
    },[props.selectedProduct]);

    // Load the templates list once (drives the picker and the read-only summary).
    useEffect(() => {
        new DynamicPricingTemplateService().list()
            .then(list => setTemplates(list || []))
            .catch(() => setTemplates([]));
    },[props.selectedProduct]);

    const sendPriceToSite = async () => {
        setLoading(true);
        productService.applyPriceSuggestion(selectedProduct.id).then(message=>{
            setLoading(false);
            setShowModal(false);
            toast.current.show({severity: 'success', summary: t('reprice.send_price_to_site'), detail: t('message_detail.successful')});
        }).catch(error => {
            setLoading(false);
            setShowModal(false);
            setError(error);
            setError(null);
        });
    };

    const getAppliedPriceSuggestions = (e) => {
        productService.getAppliedPriceSuggestions(selectedProduct.id, new Date(getWeeksAgo(2)), new Date()).then(result => {
            setAppliedSuggestions(result.map(ap => ({
                ...ap, difference: parseFloat(ap.suggested_value) - parseFloat(ap.owner_value)
            })));

            setShowHistory(true);
        });
    }

    const savePricingStrategy = () => {
        checkErrors(pricingStrategy).then(formErrors => {
            if (!formValid(formErrors)) { setFormErrors(formErrors); return; }

            setSaving(true);

            if (mode === 'template') {
                if (!selectedTemplateId) {
                    // No template selected: REMOVE the rule from this product the same
                    // way a detach does — keep the per-product absolutes (min / max /
                    // optimum), drop the rule fields and deactivate, so an active
                    // campaign strategy takes over. (To keep editable product-specific
                    // rules instead, switch to Product mode.)
                    const psToSave = {
                        min_value: pricingStrategy.min_value,
                        max_value: pricingStrategy.max_value,
                        optimum_value: pricingStrategy.optimum_value,
                        is_active: false,
                        scope: 'product',
                    };
                    // Clear the tag BEFORE saving so the parent's spread captures it.
                    props.selectedProduct.pricing_template_id = null;
                    props.savePricingStrategy(psToSave, false).then(() => {
                        setMode('product');
                        setSelectedTemplateId(null);
                        setSaving(false);
                        setEditMode(false);
                    });
                } else {
                    // Template selected: apply template rules, then save user's min/max/optimum overrides
                    const formMin = pricingStrategy.min_value;
                    const formMax = pricingStrategy.max_value;
                    const formOptimum = pricingStrategy.optimum_value;
                    new DynamicPricingTemplateService().apply(selectedTemplateId, [props.selectedProduct.id])
                        .then(res => {
                            const applied = (res && res.products && res.products[0] && res.products[0].pricing_strategy) || {};
                            const psToSave = {
                                ...defaultStrategy,
                                ...applied,
                                min_value: formMin,
                                max_value: formMax,
                                optimum_value: formOptimum,
                            };
                            delete psToSave.scope;
                            delete psToSave.reduction_direction;
                            // Set the tag BEFORE saving so the parent's setSelectedProduct
                            // spread captures the new template id (view + brief tags update).
                            props.selectedProduct.pricing_template_id = selectedTemplateId;
                            return props.savePricingStrategy(psToSave, true);
                        })
                        .then(() => {
                            setSaving(false);
                            setEditMode(false);
                        })
                        .catch(err => { setError(err); setError(null); setSaving(false); });
                }
                return;
            }

            let psToSave = {...pricingStrategy};
            if (psToSave.scope === "campaign") {
                psToSave.reduction_type = "campaign";
                psToSave.reduction_value = null;
                psToSave.is_active = false;
                psToSave.is_auto_update = false;
                psToSave.no_competitor_strategy = false;
                psToSave.guarantee_buybox = false;
                delete psToSave.scope;
                setPricingStrategy({...pricingStrategy, reduction_value: null, is_active: false, is_auto_update: false});
            } else {
                delete psToSave.scope;
            }

            psToSave.reduction_value = psToSave.reduction_value * psToSave.reduction_direction;
            delete psToSave.reduction_direction;

            // Campaign/product save detaches any template (keepTemplate=false); clear
            // the tag BEFORE saving so the parent's spread captures it (view + brief).
            props.selectedProduct.pricing_template_id = null;
            setSelectedTemplateId(null);
            props.savePricingStrategy(psToSave, false).then(() => {
                setSaving(false);
                setEditMode(false);
            }).catch(error => {
                setSaving(false);
                setError(error);
                setError(null);
            });
        });
    };

    const cancelEdit = () => {
        deriveFromProduct();
        setFormErrors({});
        setEditMode(false);
    };

    const onModeChange = (newMode) => {
        // SelectButton can fire null when clicking the already-active option.
        if (!newMode) return;
        setMode(newMode);
        if (newMode === 'campaign')
            setPricingStrategy({...pricingStrategy, scope: 'campaign'});
        else if (newMode === 'product')
            setPricingStrategy({...pricingStrategy, scope: 'product'});
        // 'template': do not change the strategy yet; the picker drives apply/remove.
    };

    const onChange = (e) =>{

        const { key, value } = getKeyValue(e);

        setFormErrors({...formErrors, [key]: checkError(key, value)});
        setPricingStrategy({...pricingStrategy, [key] : value});
    };

    const errorClass = (key) => {
        return formErrors[key] && formErrors[key].length > 0
    };

    const checkError = (key, value) => {
        let errorText = "";

        if (key === "scope") {
            errorText = value !== "campaign" && value !== "product" ? t('validations.invalid_type') : "";
        } else if (key === "min_value") {
            errorText = value < 0 ? t('validations.invalid_value') : "";
        } else if (key === "optimum_value") {
            errorText = showOptimumValue && value < 0 ? t('validations.invalid_value') : "";
        } else if (key === "reduction_type") {
            errorText = value !== "amount" && value !== "ratio" ? t('validations.invalid_type') : "";
        } else if (key === "reduction_direction") {
            errorText = !(value === 1 || value === -1) ? t('validations.invalid_value') : "";
        } else if (key === "reduction_value") {
            errorText = pricingStrategy.scope === "campaign" ? "" : (value < 0 ? t('validations.invalid_value') : "");
        } else if (key === "protection_strategy") {
            errorText = value === "" ? t('validations.invalid_value') : "";
        } else if (key === "is_active") {
            errorText = value !== true && value !== false ? t('validations.invalid_value') : "";
        } else if (key === "is_auto_update") {
            errorText = value !== true && value !== false ? t('validations.invalid_value') : "";
        } else if (key === "ignore_some_competitors") {
            errorText = value === "" ? t('validations.invalid_value') : "";
        } else if (key == "no_competitor_strategy") {
            errorText = value === "" ? t('validations.invalid_value') : "";
        }
        return errorText;
    };

    const checkErrors = async (pricingStrategy) => {

        let errors = { ...formErrors };

        Object.entries(pricingStrategy).forEach(([key, value]) => {
            errors[key] = checkError(key, value);
        });

        return errors;
    };

    let suggestion_direction = "";
    let difference = "";
    if (selectedProduct.target_price) {
        if(selectedProduct.myPrice)
        {
            if (parseFloat(selectedProduct.myPrice) > parseFloat(selectedProduct.target_price))
                suggestion_direction = "down";
            else if (parseFloat(selectedProduct.myPrice) < parseFloat(selectedProduct.target_price))
                suggestion_direction = "up";

            difference =  formatCurrency(Math.abs(parseFloat(selectedProduct.myPrice) - parseFloat(selectedProduct.target_price)), selectedProduct.currency) ;
        }
    }

    const modalYesNo = (
        <div>
            <Button label={t('yes_no.yes')} icon="pi pi-check" onClick={sendPriceToSite} />
            <Button label={t('yes_no.no')} icon="pi pi-times" onClick={() => setShowModal(false)}/>
        </div>
    );

    // Editable rule fields only in product-specific mode; template/campaign rules
    // are locked and shown as a read-only summary instead.
    const showEditableRules = mode === 'product';
    const showRuleSummary = mode === 'template' || mode === 'campaign';

    // Optimum price is a per-product override that is always shown — in every mode
    // (template / campaign / product) and in both view and edit mode.
    const showOptimumValue = true;

    // Build the locked-rule summary rows for template/campaign mode.
    const labelOf = (options, value) => { const o = options.find(x => x.value === value); return o ? o.name : value; };
    const protectionLabel = (value) => {
        const o = protectionStrategies(t).find(x => x.value === value);
        return o ? o.label : (value || '-');
    };
    // includeCompete: Compete With (ignore_some_competitors) is part of a template
    // but is NOT a campaign-level setting — it stays per-product editable in
    // campaign mode, so it's excluded from the campaign summary.
    const ruleSummaryRows = (strategy, includeCompete) => {
        if (!strategy) return [];
        if (strategy.guarantee_buybox) {
            const step = strategy.buybox_step === null || strategy.buybox_step === undefined ? 0 : strategy.buybox_step;
            const stepStr = strategy.buybox_step_type === 'percentage' ? '%' + step : step;
            const rows = [
                {label: t('reprice.pricing_mode'), value: t('reprice.mode_buybox'), tone: 'blue'},
                {label: t('reprice.buybox_step'), value: stepStr},
            ];
            if (strategy.buybox_up_step !== null && strategy.buybox_up_step !== undefined) {
                const upStepStr = strategy.buybox_up_step_type === 'percentage' ? '%' + strategy.buybox_up_step : strategy.buybox_up_step;
                rows.push({label: t('reprice.buybox_up_step'), value: upStepStr});
            }
            rows.push(
                {label: t('reprice.abandon_margin_pct'), value: (strategy.abandon_margin_pct || 0) + '%'},
                {label: t('reprice.patience_hours'), value: (strategy.patience_hours === null || strategy.patience_hours === undefined) ? t('reprice.patience_indefinite') : strategy.patience_hours},
                {label: t('reprice.buybox_upward_probe'), value: strategy.buybox_upward_probe ? t('yes_no.yes') : t('yes_no.no'), tone: strategy.buybox_upward_probe ? 'green' : 'gray'},
                {label: t('reprice.is_active'), value: strategy.is_active ? t('actions.active') : t('actions.passive'), tone: strategy.is_active ? 'green' : 'gray'},
                {label: t('reprice.is_auto_update'), value: strategy.is_auto_update ? t('reprice.auto_update') : t('reprice.manuel_update'), tone: strategy.is_auto_update ? 'blue' : 'gray'},
            );
            if (includeCompete)
                rows.push({label: t('reprice.ignore_some_competitors'), value: strategy.ignore_some_competitors ? t('reprice.only_specified') : t('reprice.all')});
            return rows;
        }
        const rows = [{label: t('reprice.protection_strategy'), value: protectionLabel(strategy.protection_strategy)}];

        const rv = strategy.reduction_value;
        const hasReduction = strategy.reduction_type && strategy.reduction_type !== 'campaign' && rv !== null && rv !== undefined;
        if (hasReduction) {
            // reduction_value may be stored signed (campaign) or as a positive
            // magnitude with a separate reduction_direction (template, post-split).
            const direction = (strategy.reduction_direction !== null && strategy.reduction_direction !== undefined)
                ? strategy.reduction_direction : (rv >= 0 ? 1 : -1);
            const dirLabel = direction >= 0 ? t('reprice.low') : t('reprice.high');
            rows.push({label: t('reprice.reduction_type'),
                value: `${labelOf(reductionTypes, strategy.reduction_type)} · ${dirLabel} · ${Math.abs(rv)}`});
        }

        rows.push(
            {label: t('reprice.is_active'), value: strategy.is_active ? t('actions.active') : t('actions.passive'), tone: strategy.is_active ? 'green' : 'gray'},
            {label: t('reprice.is_auto_update'), value: strategy.is_auto_update ? t('reprice.auto_update') : t('reprice.manuel_update'), tone: strategy.is_auto_update ? 'blue' : 'gray'},
            {label: t('reprice.no_competitor_strategy'), value: strategy.no_competitor_strategy ? t('yes_no.yes') : t('yes_no.no'), tone: strategy.no_competitor_strategy ? 'green' : 'gray'},
        );
        if (includeCompete)
            rows.push({label: t('reprice.ignore_some_competitors'), value: strategy.ignore_some_competitors ? t('reprice.only_specified') : t('reprice.all')});
        return rows;
    };
    // Compete With is editable in product & campaign modes; locked (in summary) for template.
    const showCompeteWith = mode === 'product' || mode === 'campaign';

    // Shared Save button label reflects the selected method.
    const saveLabel = mode === 'template'
        ? (selectedTemplateId ? t('reprice.save_apply_template') : t('reprice.save_no_template'))
        : (mode === 'campaign' ? t('reprice.save_apply_campaign') : t('reprice.save_product_specific'));
    // In template mode, reflect the currently picked template's rules live (even
    // before saving). In campaign mode, show the campaign's applied rules.
    const selectedTemplate = templates.find(x => x.id === selectedTemplateId);
    const summaryStrategy = mode === 'template'
        ? selectedTemplate
        : (selectedProduct.campaign && selectedProduct.campaign.pricing_strategy);
    const summaryTitle = mode === 'template'
        ? `${t('reprice.template_rules')}${selectedTemplate ? ': ' + selectedTemplate.name : ''}`
        : t('reprice.campaign_rules');

    // --- Read-only (view mode) helpers ---
    // No product-level strategy but the product's OWN campaign has an active one ->
    // the product INHERITS those rules; show them read-only (product min/max still
    // apply). Use the product's campaign, NOT the globally-selected one -- the
    // product may belong to a different campaign than the one selected in the header.
    const campaignStrategy = selectedProduct.campaign && selectedProduct.campaign.pricing_strategy;
    // A product inherits its campaign only when it has NEITHER its own strategy NOR a
    // template (a template product follows the template, not the campaign).
    const inheritsCampaign = !selectedProduct.pricing_strategy && !selectedProduct.pricing_template_id
        && !!(campaignStrategy && campaignStrategy.is_active);
    // "Has a strategy" = product-level OR template OR inherited campaign. When false,
    // nothing at all is configured and the view shows the empty-state CTA.
    const hasStrategy = !!selectedProduct.pricing_strategy || !!selectedProduct.pricing_template_id || inheritsCampaign;
    // Method tag shown at the top of view mode. An inherited (product has no own
    // strategy) campaign strategy shows the same Campaign chip.
    const methodTag = inheritsCampaign
        ? <span className="pux-chip pux-chip-blue"><i className="pi pi-megaphone" /> {t('reprice.campaign')}</span>
        : (mode === 'template'
            ? <span className="pux-chip pux-chip-violet"><i className="pi pi-bolt" /> {t('reprice.from_template')}{selectedTemplate ? `: ${selectedTemplate.name}` : ''}</span>
            : (mode === 'campaign'
                ? <span className="pux-chip pux-chip-blue"><i className="pi pi-megaphone" /> {t('reprice.campaign')}</span>
                : <span className="pux-chip pux-chip-green"><i className="pi pi-box" /> {t('reprice.product')}</span>));
    // Rule rows for view mode. Campaign (explicit scope OR inherited) reads the
    // campaign strategy; template/product read the applied strategy.
    const viewRuleRows = () => {
        if (inheritsCampaign) {
            return ruleSummaryRows(campaignStrategy, false);
        }
        if (mode === 'campaign') {
            const camp = selectedProduct.campaign && selectedProduct.campaign.pricing_strategy;
            const rows = ruleSummaryRows(camp, false);
            rows.push({label: t('reprice.ignore_some_competitors'), value: pricingStrategy.ignore_some_competitors ? t('reprice.only_specified') : t('reprice.all')});
            return rows;
        }
        return ruleSummaryRows(pricingStrategy, true);
    };
    const priceProtectionRows = () => {
        // A zero / null / undefined bound means it is not set.
        const priceRow = (label, value, icon, bg, color) => (!value)
            ? {label, value: t('product.not_set'), muted: true, icon, bg, color}
            : {label, value: formatCurrency(value, selectedProduct.currency), icon, bg, color};
        const rows = [
            priceRow(t('reprice.min_value'), pricingStrategy.min_value, 'pi pi-angle-double-down', '#dcfce7', '#16a34a'),
            priceRow(t('reprice.max_value'), pricingStrategy.max_value, 'pi pi-angle-double-up', '#dbeafe', '#2563eb'),
        ];
        if (showOptimumValue)
            rows.push(priceRow(t('reprice.optimum_value'), pricingStrategy.optimum_value, 'pi pi-bullseye', '#ede9fe', '#7c3aed'));
        return rows;
    };

    const ownerPriceTemplate = (rowData) => {
        return formatCurrency(rowData.owner_value, rowData.owner_currency);
    };
    const suggestedPriceTemplate = (rowData) => {

        let suggestion_direction = "";
        if (rowData.suggested_value) {
            if(rowData.owner_value)
            {
                if (parseFloat(rowData.owner_value) > parseFloat(rowData.suggested_value))
                    suggestion_direction = "down";
                else if (parseFloat(rowData.owner_value) < parseFloat(rowData.suggested_value))
                    suggestion_direction = "up";
            }
        }

        return <div className="p-col-8"
                    style={{color: suggestion_direction === "down" ? "red" : (suggestion_direction === "up" ? "green" : "")}}>
            <label>{formatCurrency(rowData.suggested_value, rowData.owner_currency)}</label>
            &nbsp;<i className={suggestion_direction === "down" ? "pi pi-arrow-down" : (suggestion_direction === "up" ? "pi pi-arrow-up" : "")}/>
        </div>;
    };
    const differenceTemplate = (rowData) => {
        return <label>{formatCurrency(rowData.difference, rowData.owner_currency)}</label>;
    };
    const competitorTemplate = (rowData) => {
        return<a target="_blank" rel="noopener noreferrer" href={rowData.competitor_product_url}>{rowData.competitor_min_name}</a>;
    };
    const competitorPriceTemplate = (rowData) => {
        return formatCurrency(rowData.competitor_min_value, rowData.owner_currency);
    };
    // Why the price moved. Stored as a stable code so it can be re-worded and
    // translated without touching history; anything unrecognised falls back to
    // the raw code rather than rendering blank, so a new backend reason shows
    // up as something readable instead of disappearing.
    const reasonTemplate = (rowData) => {
        if (!rowData.reason) return null;
        return <span>{t('reprice.reason.' + rowData.reason, rowData.reason)}</span>;
    };
    const createdAtTemplate = (rowData) => <span>{toDateTimeString(rowData.created_at)}</span>;

    // Result & Suggestion section — identical in view and edit mode.
    const resultSection = (
        <div className="p-col-12">
            <div className="pux-section">
                <div className="pux-section-head">
                    <span className="pux-section-title"><i className="pi pi-chart-line" /> {t('reprice.section_result')}</span>
                    <Button icon="pi pi-history" className="p-button-outlined p-button-sm" label={t('dashboard.dynamic_pricing')} onClick={(e) => getAppliedPriceSuggestions(e)} />
                </div>
                <div className="p-grid">
                    {/* What the controller is doing right now, shown whether or not there is a
                        suggestion. Both cases need it: with no suggestion it explains why, and
                        WITH one it distinguishes a retreat -- which produces a price while the
                        buybox is written off -- from an ordinary chase. Buybox campaigns only;
                        renders nothing for every other product. */}
                    {(selectedProduct.buybox_state || {}).state &&
                        <div className="p-col-12">
                            <BuyboxStateBadge buyboxState={selectedProduct.buybox_state} t={t} />
                        </div>}
                    {selectedProduct.suggested_price !== null && selectedProduct.suggested_price !== undefined ?
                        <React.Fragment>
                            {selectedProduct.pricing_suggestion &&
                                <React.Fragment>
                                    <div className="p-col-4">
                                        <label>{t('reprice.min_seller')}</label>
                                    </div>
                                    <div className="p-col-8">
                                        <label className="p-tag p-tag-warning">
                                            {selectedProduct.pricing_suggestion.competitor_min_name}&nbsp;&nbsp;->&nbsp;&nbsp;{formatCurrency(selectedProduct.pricing_suggestion.competitor_min_value, selectedProduct.currency)}
                                        </label>
                                    </div>
                                </React.Fragment>
                            }
                            <div className="p-col-4">
                                <label>{t('reprice.target_price')}</label>
                            </div>
                            <div className="p-col-8">
                                <label className={suggestion_direction === "down" ? "p-tag p-tag-danger" : (suggestion_direction === "up" ? "p-tag p-tag-success" : "")}>
                                    {formatCurrency(selectedProduct.target_price, selectedProduct.currency)}
                                    &nbsp;<i className={suggestion_direction === "down" ? "pi pi-arrow-down" : (suggestion_direction === "up" ? "pi pi-arrow-up" : "")}/>
                                    &nbsp;&nbsp;&nbsp;<label>({difference})</label>
                                </label>
                                {selectedProduct.suggested_price === selectedProduct.target_price &&
                                <Button label={t('reprice.send_price_to_site')}
                                        tooltip={autoUpdatePaused ? t('reprice.auto_update_paused_send_hint') : t('reprice.send_price_to_site_tooltip')}
                                        icon="pi pi-globe" className="p-button-outlined p-button-info" style={{marginLeft: '0.5em'}}
                                        disabled={autoUpdatePaused}
                                        onClick={() => setShowModal(true)}/>
                                }
                            </div>
                            {selectedProduct.suggested_price !== selectedProduct.target_price &&
                                <React.Fragment>
                                    <div className="p-col-4">
                                        <label>{t('reprice.suggested_price')}</label>
                                        {<Tooltip tooltip={t('product.suggested_price_tooltip')}/>}
                                    </div>
                                    <div className="p-col-8">
                                        <label className={suggestion_direction === "down" ? "p-tag p-tag-danger" : (suggestion_direction === "up" ? "p-tag p-tag-success" : "")}>
                                            {formatCurrency(selectedProduct.suggested_price, selectedProduct.currency)}
                                            &nbsp;{t('reprice.in_site_discount', {discount:((1-(selectedProduct.target_price / selectedProduct.suggested_price)) * 100).toFixed(2)})}
                                        </label>
                                        <Button label={t('reprice.send_price_to_site')}
                                                tooltip={autoUpdatePaused ? t('reprice.auto_update_paused_send_hint') : t('reprice.send_price_to_site_tooltip')}
                                                icon="pi pi-globe" className="p-button-outlined p-button-info" style={{marginLeft: '0.5em'}}
                                                disabled={autoUpdatePaused}
                                                onClick={() => setShowModal(true)}/>
                                    </div>
                                </React.Fragment>
                            }
                        </React.Fragment>
                        : <div className="p-col-12"><span className="pux-empty">{t('reprice.no_suggestion_yet')}</span></div>}
                </div>
            </div>
        </div>
    );

    // Read-only summary of the applied configuration (view mode).
    const viewSection = (
        <div className="p-grid">
            {/* Header row: method chip on the left, the edit CTA on the right. The
                inherited-campaign case keeps its CTA in the Price Protection box and
                the empty state in the Rules box, so the top button is only for products
                with their own config. */}
            {hasStrategy &&
            <div className="p-col-12" style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px', flexWrap:'wrap'}}>
                <div style={{display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap'}}>
                    {methodTag}
                    {autoUpdatePaused &&
                    <span className="pux-chip pux-chip-amber"><i className="pi pi-pause" /> {t('reprice.auto_update_paused')}</span>}
                </div>
                <div style={{display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap'}}>
                    {/* Sending on/off, not the rule: the product keeps being priced by
                        the rule shown on the left either way. */}
                    <span style={{display:'flex', alignItems:'center', gap:'6px'}}>
                        <span style={{fontSize:'0.9em'}}>{t('reprice.auto_update_sending')}</span>
                        <InputSwitch checked={!autoUpdatePaused} disabled={savingAutoUpdate}
                                     onChange={(e) => toggleAutoUpdatePaused(!e.value)}/>
                        <Tooltip tooltip={t('reprice.auto_update_sending_tooltip')}/>
                    </span>
                    {!inheritsCampaign &&
                    <Button label={t('reprice.set_dynamic_pricing')} icon="pi pi-pencil"
                            className="p-button-outlined p-button-success" onClick={() => setEditMode(true)}/>}
                </div>
            </div>}

            {/* Price Protection always shows; when nothing is configured it renders
                "Not set" tiles rather than being hidden. */}
            <div className="p-col-12 p-md-6">
                <div className="pux-section" style={{height:'100%'}}>
                    <div className="pux-section-head">
                        <span className="pux-section-title"><i className="pi pi-shield" /> {t('reprice.section_price_protection')}</span>
                    </div>
                    <div className="pux-protect-tiles">
                        {priceProtectionRows().map((row, i) => (
                            <div className="pux-metric" key={i}>
                                <span className="pux-metric-icon" style={{background: row.bg, color: row.color}}><i className={row.icon} /></span>
                                <div>
                                    <div className={"pux-metric-val" + (row.muted ? " muted" : "")}>{row.value}</div>
                                    <div className="pux-metric-lab">{row.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Inherited campaign: rules come from the campaign but there is no
                        product-level min/max yet -> offer to set it right here. Opens the
                        editor in From Campaign mode (see deriveFromProduct). */}
                    {inheritsCampaign &&
                    <div style={{marginTop:'16px', display:'flex', justifyContent:'center'}}>
                        <Button label={t('reprice.set_price_protection')} icon="pi pi-pencil"
                                className="p-button-success p-button-lg" onClick={() => setEditMode(true)}/>
                    </div>}
                </div>
            </div>

            {/* Rules always shows; when nothing is configured the box holds the
                primary "Set Dynamic Pricing" CTA instead of rule rows. */}
            <div className="p-col-12 p-md-6">
                <div className="pux-section" style={{height:'100%'}}>
                    <div className="pux-section-head">
                        <span className="pux-section-title"><i className="pi pi-cog" /> {t('reprice.section_rules')}</span>
                    </div>
                    {hasStrategy
                        ? viewRuleRows().map((row, i) => (
                            <div className="pux-rule-row" key={i}>
                                <span className="pux-rule-label">{row.label}</span>
                                {row.tone
                                    ? <span className={"pux-chip pux-chip-" + row.tone}>{row.value}</span>
                                    : <span className="pux-rule-value">{row.value}</span>}
                            </div>
                        ))
                        : <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'14px', padding:'28px 12px', textAlign:'center'}}>
                            <span className="pux-empty">{t('reprice.no_dynamic_pricing_yet')}</span>
                            <Button label={t('reprice.set_dynamic_pricing')} icon="pi pi-pencil"
                                    className="p-button-success p-button-lg" onClick={() => setEditMode(true)}/>
                          </div>}
                </div>
            </div>

            {/* Suggestion/result is hidden when nothing is configured AND when the
                product only inherits the campaign (no product-level setup yet). */}
            {hasStrategy && !inheritsCampaign && resultSection}

            <Dialog header={t('reprice.send_price_to_site')} footer={modalYesNo} visible={showModal}
                    style={{maxWidth: '50vw', minWidth: '20vw'}} modal={true} onHide={() => setShowModal(false)}>
                {t('dialogs.confirm_send_price_message')}
            </Dialog>
        </div>
    );

    // Written by the ikas integration, never by the merchant: another active
    // product of the account owns the same ikas variant, so this one's price is
    // not sent. It keeps its suggestion; only the send is held.
    const priceSendHold = getActivePriceSendHold(props.selectedProduct);

    return <div className="p-grid">

        {priceSendHold &&
        <div className="p-col-12">
            <div className="p-inline-message p-component p-inline-message-warn"
                 style={{display: 'flex', width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 8}}>
                <span className="p-inline-message-icon pi pi-exclamation-triangle"/>
                <span className="p-inline-message-text">
                    {t('reprice.price_send_hold_duplicate', {
                        key: priceSendHold.key, code: priceSendHold.code,
                        campaign: priceSendHold.winner_campaign_name || '-'})}
                    {priceSendHold.checkedAt &&
                    <> {t('reprice.price_send_hold_checked_at', {
                        date: priceSendHold.checkedAt.toLocaleDateString(i18n.language)})}</>}
                    {priceSendHold.winner_product_id &&
                    <> <a target="_blank" rel="noopener noreferrer"
                          href={"/#/productdetail?id=" + priceSendHold.winner_product_id}>
                        {t('reprice.price_send_hold_open_owner')}</a></>}
                </span>
            </div>
        </div>}

        {selectedCampaign?.settings?.include_out_of_stock_prices &&
        <div className="p-col-12">
            <Message severity="info" style={{display: 'flex', width: '100%'}}
                     text={t('reprice.include_out_of_stock_prices_notice')}/>
        </div>}

        {showHistory &&
        <Dialog header={t('dashboard.dynamic_pricing')} visible={true} modal dismissableMask
                style={{ width: '60vw', maxWidth: '900px' }} contentStyle={{ maxHeight: '60vh', overflow: 'auto' }}
                onHide={() => setShowHistory(false)} >
            <DataTable value={appliedSuggestions} responsive emptyMessage={t('actions.empty_message')}
                       sortField="created_at" sortOrder="-1">
                <Column body={IndexTemplate} style={{width:'3em'}} />
                <Column field="owner_value" body={ownerPriceTemplate} header={t('product.myprice')} />
                <Column field="suggested_value" header={t('reprice.applied_price')} body={suggestedPriceTemplate} />
                <Column field="difference" header={t('reprice.difference')} body={differenceTemplate} />
                <Column field="competitor_min_name" body={competitorTemplate} header={t('product.comp')} />
                <Column field="competitor_min_value" body={competitorPriceTemplate} header={t('product.comp_min')} />
                <Column field="reason" header={t('reprice.reason_header')} body={reasonTemplate} />
                <Column field="created_at" header={t('reprice.applied_at')} body={createdAtTemplate} />
            </DataTable>
        </Dialog >}

        {loading ?
            <div className="p-col-12" style={{textAlign:'center'}}>
                <Loading/>
            </div>
            :
            viewSection
        }

        <Dialog header={t('reprice.set_dynamic_pricing')} visible={editMode} modal={true}
                style={{width:'680px', maxWidth:'95vw'}} onHide={cancelEdit}>
            <div className="p-grid">
                <div className="p-col-12">
                    <div className="pux-mode-cards">
                        {[
                            {value:'template', icon:'pi pi-bolt', color:'violet', title:t('reprice.from_template'), desc:t('reprice.mode_template_desc')},
                            {value:'campaign', icon:'pi pi-megaphone', color:'blue', title:t('reprice.campaign'), desc:t('reprice.mode_campaign_desc')},
                            {value:'product', icon:'pi pi-box', color:'green', title:t('reprice.product'), desc:t('reprice.mode_product_desc')},
                        ].map(m =>
                            <div key={m.value} className={"pux-mode-card" + (mode === m.value ? " selected " + m.color : "")} onClick={() => onModeChange(m.value)}>
                                <div className="pux-mode-card-icon"><i className={m.icon} /></div>
                                <div className="pux-mode-card-title">{m.title}</div>
                                <div className="pux-mode-card-desc">{m.desc}</div>
                            </div>
                        )}
                    </div>
                </div>

                {mode === 'template' &&
                <div className="p-col-12">
                    <div className="pux-template-picker">
                        <label className="pux-template-picker-label"><i className="pi pi-bolt" /> {t('reprice.templates')}</label>
                        <Dropdown value={selectedTemplateId}
                                  options={[{label: t('reprice.no_template'), value: null}, ...templates.map(tpl => ({label: tpl.name, value: tpl.id}))]}
                                  onChange={(e) => setSelectedTemplateId(e.value)}
                                  placeholder={t('reprice.select_template')} />
                    </div>
                </div>}

                <div className="p-col-12">
                    <div className="pux-section">
                        <div className="pux-section-head">
                            <span className="pux-section-title"><i className="pi pi-shield" /> {t('reprice.section_price_protection')}</span>
                        </div>
                        <div className="p-grid">
                            <div className="p-col-4">
                                <label htmlFor="min_value">{t('reprice.min_value')}</label>
                                <Tooltip tooltip={t('reprice.min_value_tooltip')}/>
                            </div>
                            <div className="p-col-8">
                                <Number id="min_value" name="min_value" value={pricingStrategy.min_value} onChange={(e) => onChange(e)} className={errorClass("min_value")}/>
                                {errorClass("min_value") && (<Message severity="error" text={formErrors.min_value}/>)}
                            </div>
                            <div className="p-col-4">
                                <label htmlFor="max_value">{t('reprice.max_value')}</label>
                                <Tooltip tooltip={t('reprice.max_value_tooltip')}/>
                            </div>
                            <div className="p-col-8">
                                <Number id="max_value" name="max_value" value={pricingStrategy.max_value} onChange={(e) => onChange(e)} className={errorClass("max_value")}/>
                                {errorClass("max_value") && (<Message severity="error" text={formErrors.max_value}/>)}
                            </div>
                            {showOptimumValue &&
                            <React.Fragment>
                                <div className="p-col-4">
                                    <label htmlFor="optimum_value">{t('reprice.optimum_value')}</label>
                                    <Tooltip tooltip={t('reprice.optimum_value_tooltip')}/>
                                </div>
                                <div className="p-col-8">
                                    <Number id="optimum_value" name="optimum_value" value={pricingStrategy.optimum_value} onChange={(e) => onChange(e)} className={errorClass("optimum_value")}/>
                                    {errorClass("optimum_value") && (<Message severity="error" text={formErrors.optimum_value}/>)}
                                </div>
                            </React.Fragment>}
                        </div>
                    </div>
                </div>

                <div className="p-col-12">
                    <div className="pux-section">
                        <div className="pux-section-head">
                            <span className="pux-section-title"><i className="pi pi-cog" /> {t('reprice.section_rules')}</span>
                        </div>
                        <div className="p-grid">
                            {showEditableRules &&
                            <React.Fragment>
                                {isBuyboxCampaign &&
                                <div className="p-col-12">
                                    <label style={{display:'block', marginBottom:6}}>{t('reprice.pricing_mode')}</label>
                                    <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                                        {pricingModes.map(m => {
                                            const active = !!pricingStrategy.guarantee_buybox === m.value;
                                            return (
                                                <div key={String(m.value)} onClick={() => setPricingStrategy({...pricingStrategy, guarantee_buybox: m.value})}
                                                     style={{flex:'1 1 220px', minWidth:200, cursor:'pointer', borderRadius:10, padding:'12px 14px',
                                                             border:'2px solid ' + (active ? '#2563eb' : '#e5e7eb'),
                                                             background: active ? '#eff6ff' : '#fff',
                                                             boxShadow: active ? '0 1px 4px rgba(37,99,235,.15)' : 'none'}}>
                                                    <div style={{fontWeight:700, color:'#111827', marginBottom:4}}>{m.name}</div>
                                                    <div style={{fontSize:'.8rem', color:'#6b7280'}}>{m.desc}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>}

                                <div className="p-col-12">
                                    <PricingModeGuide t={t} mode={!!(isBuyboxCampaign && pricingStrategy.guarantee_buybox)}/>
                                </div>

                                {!(isBuyboxCampaign && pricingStrategy.guarantee_buybox) &&
                                <React.Fragment>
                                <SectionHeader label={t('reprice.section_strategy')} first/>
                                <div className="p-col-4">
                                    <label>{t('reprice.reduction_type')}</label>
                                </div>
                                <div className="p-col-8">
                                    <SelectButton name="reduction_type" value={pricingStrategy.reduction_type} options={reductionTypes} optionLabel="name" onChange={onChange} />
                                    {errorClass("reduction_type") && (<Message severity="error" text={formErrors.reduction_type}/>)}
                                </div>
                                <div className="p-col-4">
                                    <label>{t('reprice.reduction_direction')}</label>
                                </div>
                                <div className="p-col-8">
                                    <SelectButton name="reduction_direction" value={pricingStrategy.reduction_direction} options={reductionDirections} optionLabel="name" onChange={onChange} />
                                    {errorClass("reduction_direction") && (<Message severity="error" text={formErrors.reduction_direction}/>)}
                                </div>
                                <div className="p-col-4">
                                    <label htmlFor="reduction_value">{t('reprice.reduction_value')}</label>
                                </div>
                                <div className="p-col-8">
                                    <Number id="reduction_value" name="reduction_value" value={pricingStrategy.reduction_value} onChange={(e) => onChange(e)} className={errorClass("reduction_value")}/>
                                    {errorClass("reduction_value") && (<Message severity="error" text={formErrors.reduction_value}/>)}
                                </div>

                                <SectionHeader label={t('reprice.section_protection')}/>
                                <div className="p-col-4">
                                    <label>{t('reprice.protection_strategy')}</label>
                                    <Tooltip tooltip={t('reprice.protection_strategy_tooltip')}/>
                                </div>
                                <div className="p-col-8">
                                    <Dropdown inputId="protection_strategy" name="protection_strategy" options={protectionStrategies(t)} value={pricingStrategy.protection_strategy} onChange={onChange} />
                                    {errorClass("protection_strategy") && (<Message severity="error" text={formErrors.protection_strategy}/>)}
                                </div>
                                <div className="p-col-4">
                                    <label>{t('reprice.no_competitor_strategy')}</label>
                                    <Tooltip tooltip={t('reprice.no_competitor_strategy_tooltip')}/>
                                </div>
                                <div className="p-col-8">
                                    <SelectButton name="no_competitor_strategy" value={pricingStrategy.no_competitor_strategy} options={noCompetitorTypes} optionLabel="name" onChange={onChange} />
                                    {errorClass("no_competitor_strategy") && (<Message severity="error" text={formErrors.no_competitor_strategy}/>)}
                                </div>
                                </React.Fragment>}
                                {isBuyboxCampaign && pricingStrategy.guarantee_buybox &&
                                <React.Fragment>
                                <SectionHeader label={t('reprice.section_no_buybox')} first/>
                                <div className="p-col-4">
                                    <label htmlFor="buybox_step">{t('reprice.buybox_step')}</label>
                                    <Tooltip tooltip={t('reprice.buybox_step_tooltip')}/>
                                </div>
                                <div className="p-col-8" style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                                    <Number id="buybox_step" name="buybox_step" value={pricingStrategy.buybox_step} onChange={(e) => onChange(e)} />
                                    <SelectButton name="buybox_step_type" value={pricingStrategy.buybox_step_type} options={buyboxStepTypes} optionLabel="name" onChange={onChange} />
                                </div>

                                <SectionHeader label={t('reprice.section_has_buybox')}/>
                                <div className="p-col-4">
                                    <label htmlFor="buybox_upward_probe">{t('reprice.buybox_upward_probe')}</label>
                                    <Tooltip tooltip={t('reprice.buybox_upward_probe_tooltip')}/>
                                </div>
                                <div className="p-col-8">
                                    <Checkbox inputId="buybox_upward_probe" name="buybox_upward_probe"
                                              checked={!!pricingStrategy.buybox_upward_probe} onChange={onChange} />
                                </div>

                                <div className="p-col-4">
                                    <label htmlFor="buybox_up_step">{t('reprice.buybox_up_step')}</label>
                                    <Tooltip tooltip={t('reprice.buybox_up_step_tooltip')}/>
                                </div>
                                <div className="p-col-8" style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                                    <InputNumber id="buybox_up_step" name="buybox_up_step" value={pricingStrategy.buybox_up_step}
                                                 mode="decimal" minFractionDigits={0} maxFractionDigits={2} min={0}
                                                 onValueChange={(e) => onNumberFieldChange('buybox_up_step', e.value)} />
                                    <SelectButton name="buybox_up_step_type" value={pricingStrategy.buybox_up_step_type} options={buyboxUpStepTypes} optionLabel="name" onChange={onChange} />
                                </div>

                                <SectionHeader label={t('reprice.section_cheap_seller')}/>
                                <div className="p-col-4">
                                    <label htmlFor="abandon_margin_pct">{t('reprice.abandon_margin_pct')}</label>
                                    <Tooltip tooltip={t('reprice.abandon_margin_pct_tooltip')}/>
                                </div>
                                <div className="p-col-8">
                                    <InputNumber id="abandon_margin_pct" name="abandon_margin_pct" value={pricingStrategy.abandon_margin_pct}
                                                 mode="decimal" minFractionDigits={0} maxFractionDigits={2} min={0} suffix="%"
                                                 onValueChange={(e) => onNumberFieldChange('abandon_margin_pct', e.value, true)} />
                                </div>

                                <div className="p-col-4">
                                    <label htmlFor="patience_hours">{t('reprice.patience_hours')}</label>
                                    <Tooltip tooltip={t('reprice.patience_hours_tooltip')}/>
                                </div>
                                <div className="p-col-8">
                                    <InputNumber id="patience_hours" name="patience_hours" value={pricingStrategy.patience_hours}
                                                 mode="decimal" minFractionDigits={0} maxFractionDigits={2} min={0}
                                                 onValueChange={(e) => onNumberFieldChange('patience_hours', e.value)} />
                                </div>
                                </React.Fragment>}

                                <SectionHeader label={t('reprice.section_status')}/>
                                <div className="p-col-4">
                                    <label>{t('reprice.is_active')}</label>
                                </div>
                                <div className="p-col-8">
                                    <SelectButton name="is_active" value={pricingStrategy.is_active} options={statuses} optionLabel="name" onChange={onChange} />
                                    {errorClass("is_active") && (<Message severity="error" text={formErrors.is_active}/>)}
                                </div>
                                <div className="p-col-4">
                                    <label>{t('reprice.is_auto_update')}</label>
                                    <Tooltip tooltip={t('reprice.send_price_to_site_tooltip')}/>
                                </div>
                                <div className="p-col-8">
                                    <SelectButton name="is_auto_update" value={pricingStrategy.is_auto_update} options={updateTypes} optionLabel="name" onChange={onChange} />
                                    {errorClass("is_auto_update") && (<Message severity="error" text={formErrors.is_auto_update}/>)}
                                </div>
                            </React.Fragment>}
                            {showCompeteWith &&
                            <React.Fragment>
                                <div className="p-col-4">
                                    <label>{t('reprice.ignore_some_competitors')}</label>
                                    <Tooltip tooltip={t('reprice.ignore_some_competitors_tooltip')}/>
                                </div>
                                <div className="p-col-8">
                                    <SelectButton name="ignore_some_competitors" value={pricingStrategy.ignore_some_competitors} options={competitors} optionLabel="name" onChange={onChange} />
                                    {errorClass("ignore_some_competitors") && (<Message severity="error" text={formErrors.ignore_some_competitors}/>)}
                                </div>
                            </React.Fragment>}
                            {showRuleSummary && summaryStrategy &&
                            <div className="p-col-12">
                                <div className="pux-rule-summary">
                                    <div className="pux-rule-summary-title">
                                        <i className="pi pi-lock" /> {summaryTitle}
                                    </div>
                                    {ruleSummaryRows(summaryStrategy, mode === 'template').map((row, i) => (
                                        <div className="pux-rule-row" key={i}>
                                            <span className="pux-rule-label">{row.label}</span>
                                            <span className="pux-rule-value">{row.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>}
                        </div>
                    </div>
                </div>

                <div className="p-col-12" style={{display:'flex', justifyContent:'flex-end', gap:'8px'}}>
                    <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-outlined p-button-secondary"
                            disabled={saving} onClick={cancelEdit}/>
                    <Button label={saveLabel} icon="pi pi-save" className="p-button-success"
                            loading={saving} onClick={savePricingStrategy}/>
                </div>
            </div>
        </Dialog>
        </div>;

};

// Sub-header inside the Rules card, mirroring the section-divider style used by
// the campaign wizard's "Strategy" / "Status" headers.
const SectionHeader = ({label, first}) => (
    <div className="p-col-12">
        <div style={{fontWeight: 700, color: '#374151', marginTop: first ? 4 : 8, borderBottom: '1px solid #eef2f7', paddingBottom: 4}}>
            {label}
        </div>
    </div>
);

export default ProductReprice;
